import React, { useEffect, useState } from "react";
import { Link, useStaticQuery, graphql } from "gatsby";
import styled from "styled-components";
import Logo from "../images/ep_logo.svg";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faHeart } from "@fortawesome/free-regular-svg-icons";
import { faShoppingCart } from "@fortawesome/free-solid-svg-icons";
import Image from "./image";
import { GlobalStateContext } from "../context/global-context-provider";
import PropTypes from "prop-types";
import Modal from "./modal";
import MiniCart from "./minicart";
import { ProductContext } from "./layout";
import { _ } from "../utils/i18n";
import PrivateOrBusiness from "./private-business";
import { updateDataLayer } from "../utils/helpers";

const StaticLogo = styled.img`
  height: 50px;
  object-fit: contain;
  @media (max-width: 1920px) {
    padding-left: 2rem;
  }
`;

const LogoWrapper = styled.div`
  display: ${({ $sideMenu }) => ($sideMenu ? "none" : "block")};
  width: 50%;
  @media (min-width: ${({ theme }) => theme.breakpoints.wideTablet}) {
    display: none;
  }
  @media (max-width: ${({ theme }) => theme.breakpoints.wideTablet}) {
    width: fit-content;
  }
`;

const LogoImage = styled(Image)`
  object-fit: contain;
  max-height: 50px;
  @media (max-width: 1920px) {
    margin-left: 2rem;
  }
  @media (max-width: ${({ theme }) => theme.breakpoints.wideTablet}) {
    margin-left: 0;
  }
`;

const IconsWrapper = styled.div`
  display: flex;
  flex-flow: ${({ $sideMenu }) => ($sideMenu ? "column nowrap" : "row")};
  justify-content: flex-end;
  width: 100%;
  gap: 0.3rem;
  @media (max-width: ${({ theme }) => theme.breakpoints.wideTablet}) {
    width: fit-content;
  }
  @media (max-width: ${({ theme }) => theme.breakpoints.phone}) {
    width: 150px;
  }
`;

const IconDiv = styled.div`
  width: ${({ $sideMenu }) => ($sideMenu ? "220px" : "fit-content")};
  display: flex;
  align-items: center;
  justify-content: ${({ $sideMenu }) => ($sideMenu ? "flex-start" : "center")};
  padding-block: ${({ $sideMenu }) => ($sideMenu ? "0.75rem" : "0")};
  @media (min-width: ${({ theme }) => theme.breakpoints.wideTablet}) {
    width: 220px;
  }
`;

const Icons = styled(FontAwesomeIcon)`
  padding-inline: 0.3rem;
  transition: transform 0.3s ease-in-out;
  color: var(--color-black);
  @media (max-width: ${({ theme }) => theme.breakpoints.wideTablet}) {
    margin: 0;
  }
  @media (hover: hover) {
    &:hover {
      transform: scale(1.2);
    }
  }
`;

const IconText = styled.div`
  @media (max-width: ${({ theme }) => theme.breakpoints.wideTablet}) {
    display: ${({ $sideMenu }) => ($sideMenu ? "block" : "none")};
    margin-left: ${({ $sideMenu }) => ($sideMenu ? "0.5rem" : "0")};
  }
  @media (hover: hover) {
    &:hover {
      text-decoration: underline;
    }
  }
`;

const NrOfItems = styled.div`
  margin-left: 0.3rem;
  @media (max-width: ${({ theme }) => theme.breakpoints.wideTablet}) {
    margin-left: ${({ $sideMenu }) => ($sideMenu ? "0.3rem" : "0")};
  }
  p {
    margin: 0;
  }
`;

const TopHeaderWrapperChild = styled.div`
  width: 100%;
  padding-inline: 32px;
  justify-content: ${({ $sideMenu }) =>
    $sideMenu ? "flex-start" : "flex-end"};
  display: flex;
  align-items: center;
  flex-wrap: ${({ $sideMenu }) => ($sideMenu ? "wrap" : "nowrap")};
  @media (max-width: ${({ theme }) => theme.breakpoints.wideTablet}) {
    justify-content: space-between;
    padding: 1.5rem 1rem 1rem 1rem;
    width: 100%;
  }
  @media (max-width: 350px) {
    flex-direction: column;
    width: 70%;
  }
  @media (max-width: ${({ theme }) => theme.breakpoints.phone}) {
    flex-wrap: wrap;
  }
`;

const Links = styled(Link)`
  text-decoration: none;
`;

const CartButton = styled.button`
  background-color: transparent;
  border: none;
  font-size: 1rem;
  padding-inline: 0;
  color: var(--color-black);
`;

const TopInModal = styled.div`
  background-color: var(--color-body);
  color: var(--color-black);
  height: 5.5rem;
  display: flex;
  align-items: center;
  padding-inline: 2rem;
  margin-bottom: 1rem;
`;

const ChildWrapper = styled.div`
  display: flex;
  align-items: center;
  height: 100%;
  padding-inline: 2rem;
`;
const CartLink = styled(Link)`
  text-decoration: none;
`;
const ProductCount = styled.span`
  font-weight: 500;
  font-size: 1rem;
  color: rgba(54, 54, 54, 0.6);
`;

const TopHeader = ({ sideMenu }) => {
  const { wp, favouritesPage } = useStaticQuery(graphql`
    query TopHeaderQuery {
      wp {
        wcSettings {
          cartUrl
        }
        crbThemeOptions {
          crbLogoHeaderMobile {
            media {
              altText
              sourceUrl
              localFile {
                publicURL
                childImageSharp {
                  gatsbyImageData(
                    height: 40
                    layout: FIXED
                    placeholder: NONE
                    transformOptions: { fit: CONTAIN }
                  )
                }
              }
            }
          }
          crbSearchbarEnabled
          crbPaddingBottom
          crbPaddingLeft
          crbPaddingRight
          crbPaddingTop
        }
      }
      favouritesPage: allWpPage(filter: { isFavouriteListPage: { eq: true } }) {
        nodes {
          title
          id
          uri
        }
      }
    }
  `);

  const { cart } = React.useContext(GlobalStateContext);
  const [totalQtyOfProducts, setTotalQtyOfProducts] = useState(0);
  const [modalOpen, setModalOpen] = React.useState(false);
  const { favouriteList } = React.useContext(ProductContext);
  const [privateOrBusiness, setPrivateOrBusiness] = useState(
    typeof window !== "undefined" && localStorage.getItem("private-business")
  );

  useEffect(() => {
    setTotalQtyOfProducts(
      cart.reduce((totalQtyOfProducts, item) => {
        return totalQtyOfProducts + item.quantity;
      }, 0)
    );
  }, [cart]);

  useEffect(() => {
    if (!modalOpen) {
      return;
    }

    updateDataLayer(async () =>
      window.dataLayer.push({
        event: "view_cart",
        ecommerce: {
          currency: wp.wcSettings.currency,
          value: cart.reduce(
            (acc, item) => acc + item.priceRaw * item.quantity,
            0
          ),
          items: cart.map((item) => ({
            item_id: item?.id,
            item_name: item?.name,
            item_category: item?.category,
            item_brand: item?.brand,
            price: item?.priceRaw,
            quantity: item?.quantity,
          })),
        },
      })
    );
  }, [modalOpen]);

  const checkPrivateOrBusiness = () => {
    setPrivateOrBusiness(localStorage.getItem("private-business"));
  };

  useEffect(() => {
    window.addEventListener("storage", checkPrivateOrBusiness);
    return () => removeEventListener("storage", checkPrivateOrBusiness);
  }, []);

  return (
    <>
      <TopHeaderWrapperChild className={privateOrBusiness} $sideMenu={sideMenu}>
        <LogoWrapper $sideMenu={sideMenu}>
          <Link to="/">
            {wp?.crbThemeOptions?.crbLogoHeaderMobile?.media ? (
              <LogoImage
                alt="Logotype"
                image={wp?.crbThemeOptions?.crbLogoHeaderMobile?.media ?? ""}
                loading="eager"
                src={
                  wp?.crbThemeOptions?.crbLogoHeaderMobile?.media.sourceUrl ??
                  ""
                }
              />
            ) : (
              <StaticLogo alt="Logotype" src={Logo} />
            )}
          </Link>
        </LogoWrapper>
        <PrivateOrBusiness $sideMenu={sideMenu} />
        {privateOrBusiness === "private" && (
          <>
            <IconsWrapper $sideMenu={sideMenu}>
              <Links to={favouritesPage?.nodes[0]?.uri}>
                <IconDiv $sideMenu={sideMenu}>
                  <Icons icon={faHeart} size="lg" />
                  <IconText $sideMenu={sideMenu}>
                    {_("Min huskeliste")}
                  </IconText>
                  <NrOfItems $sideMenu={sideMenu}>
                    {favouriteList.length > 0 ? (
                      <p>({favouriteList.length})</p>
                    ) : (
                      "(0)"
                    )}
                  </NrOfItems>
                </IconDiv>
              </Links>

              {sideMenu ? (
                <CartLink to={wp.wcSettings.cartUrl}>
                  <IconDiv $sideMenu={sideMenu}>
                    <Icons size="1x" icon={faShoppingCart} />
                    <IconText $sideMenu={sideMenu}>{_("Handlekurv")}</IconText>
                    <NrOfItems $sideMenu={sideMenu}>
                      {cart.length > 0 ? (
                        <p>({totalQtyOfProducts})</p>
                      ) : (
                        <p>(0)</p>
                      )}
                    </NrOfItems>
                  </IconDiv>
                </CartLink>
              ) : (
                <CartButton
                  onClick={() => {
                    setModalOpen(true);
                  }}
                >
                  <IconDiv $sideMenu={sideMenu}>
                    <Icons size="1x" icon={faShoppingCart} />
                    <IconText $sideMenu={sideMenu}>{_("Handlekurv")}</IconText>
                    <NrOfItems $sideMenu={sideMenu}>
                      {cart.length > 0 ? (
                        <p>({totalQtyOfProducts})</p>
                      ) : (
                        <p>(0)</p>
                      )}
                    </NrOfItems>
                  </IconDiv>
                </CartButton>
              )}
            </IconsWrapper>
          </>
        )}
      </TopHeaderWrapperChild>
      <Modal
        isOpen={modalOpen}
        animateFrom="right"
        variant="small"
        onClose={() => setModalOpen(false)}
      >
        {totalQtyOfProducts > 0 ? (
          <>
            <TopInModal>
              <h1>
                {_("Handlekurv")}
                <ProductCount>{` (${totalQtyOfProducts})`}</ProductCount>
              </h1>
            </TopInModal>
            <MiniCart onClose={() => setModalOpen(false)} />
          </>
        ) : (
          <ChildWrapper>
            <MiniCart onClose={() => setModalOpen(false)} />
          </ChildWrapper>
        )}
      </Modal>
    </>
  );
};

TopHeader.propTypes = {
  sideMenu: PropTypes.bool,
};

export default TopHeader;
